// The board as a list (issue 1175): every token this person can see — read from their own projected state, so a
// hidden token is absent here as it is absent from their board — each with its name as the board shows it and where
// it stands in squares. A token of their own is selected with Enter, stepped a square by the arrow keys (the board's
// own rule, `board/keyStep.ts`: the same `object.move` a drag sends, the same refusals), and its menu opened with the
// Menu key, Shift+F10 or its Menu button. Each step's new square, or the refusal, is said by the shell's live region.
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { boardExtent, dmEncounterView, type DmObject, type Encounter, type PlayerEncounter, type Size, type Wall } from '@dragons-table/vtt-shared';
import { boardBus } from '../../board/bus';
import { keyStep, mayStep, squareOf, squareWords, type StepThing } from '../../board/keyStep';
import { useBoardMap } from '../../board/sceneMap';
import { selection } from '../../board/selection';
import { useTableState } from '../../table/TableState';
import { announce } from '../Announcer';
import { BTN } from '../ui';

const btn = BTN;
const STEPS: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };

type ListThing = StepThing & { hidden?: boolean; size: Size };

function useSelection(): ReadonlySet<string> {
  const [s, setS] = useState(selection.get());
  useEffect(() => selection.subscribe(setS), []);
  return s;
}

export function TokensPanel() {
  const { store, role, act, powers, setNotice, viewingAs } = useTableState();
  const map = useBoardMap();
  const selected = useSelection();
  const extent = boardExtent(map);
  const isDm = role === 'dm' && !viewingAs;
  const raw = store.state.encounter as Encounter | PlayerEncounter | undefined;
  const world = {
    grid: store.state.grid, extent, walls: (isDm ? (store.state as { walls?: Wall[] }).walls ?? [] : []) as Wall[], isDm,
    movesAny: !!powers?.includes('tokens'), placing: isDm && store.state.placing === true,
    encounter: raw ? (isDm ? dmEncounterView(raw as Encounter) : (raw as PlayerEncounter)) : null,
  };
  const things = useMemo(() => (Object.values(store.state.objects) as unknown as ListThing[])
    .filter((o) => o.kind === 'token')
    .map((o) => ({ o, at: squareOf(o, store.state.grid, extent) }))
    // By name, so a step never reorders the list under the focus (a moved DOM node loses it).
    .sort((a, b) => (a.o.name ?? '').localeCompare(b.o.name ?? '') || a.o.id.localeCompare(b.o.id)),
  [store.state.objects, store.state.grid, extent.width, extent.height]); // eslint-disable-line react-hooks/exhaustive-deps

  const nameOf = (o: ListThing) => o.name ?? 'Unnamed token';
  const step = (o: ListThing, di: number, dj: number) => {
    selection.replace([o.id]);
    const r = keyStep(o, di, dj, world);
    if (!r) { setNotice(`${nameOf(o)} is locked.`); return; }
    if ('say' in r) { setNotice(r.say); return; }
    const to = squareOf({ ...o, x: r.to.x, y: r.to.y }, store.state.grid, extent);
    // Said once the table has it: a refusal is the table's notice, which the live region says instead.
    act(r.body, (reply) => { if (reply.ok) announce(`${nameOf(o)} moved to ${squareWords(to)}.`); });
  };
  const openMenu = (o: ListThing, el: HTMLElement) => {
    selection.replace([o.id]);
    const r = el.getBoundingClientRect();
    boardBus.openTokenMenu({ id: o.id, x: r.left + r.width / 2, y: r.bottom });
  };
  const onKey = (o: ListThing, own: boolean) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) && own) { e.preventDefault(); openMenu(o, e.currentTarget); return; }
    const s = STEPS[e.key];
    if (!s) return;
    e.preventDefault();
    // Someone else's is answered with the board's words ("… is not yours to move.").
    step(o, s[0], s[1]);
  };

  return (
    <div className="space-y-2 p-2 text-sm" data-testid="tokens">
      <p className="text-xs text-stone-400" id="tokens-help">
        The tokens you can see, by name. On one of yours: Enter selects it, the arrow keys move it a square, the Menu key opens its menu.
      </p>
      {things.length === 0 && <p className="text-xs text-stone-400">No tokens you can see.</p>}
      <ul aria-label="Tokens you can see" className="space-y-1">
        {things.map(({ o, at }) => {
          const own = mayStep(o, world);
          const words = `${nameOf(o)}, ${squareWords(at)}${o.mine && !isDm ? ', yours' : ''}${isDm && (o as unknown as DmObject).hidden ? ', hidden' : ''}${selected.has(o.id) ? ', selected' : ''}`;
          return (
            <li key={o.id} className="flex flex-wrap items-center gap-1" data-testid={`token-row-${o.id}`} data-name={o.name ?? ''}>
              <button type="button" className={`${btn} flex-1 text-left ${selected.has(o.id) ? 'border-amber-500' : ''}`} aria-describedby={own ? 'tokens-help' : undefined}
                aria-pressed={selected.has(o.id)} onClick={() => { selection.replace([o.id]); boardBus.focus(o.id); }} onKeyDown={onKey(o, own)}>
                {words}
              </button>
              {own && <button type="button" className={btn} aria-label={`Menu for ${nameOf(o)}`} onClick={(e) => openMenu(o, e.currentTarget)}>Menu</button>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
